
import { CrudService } from "../core/services/crudService";
import { Settings } from "../core/services/configService";
import Vue from "vue";
import { LocalStorage } from "../core/localStorage";
import { BookingPaymentType } from "../core/constants";
import CheckoutPayment from "@shared/components/checkout/checkoutPayment-v2.vue";
import { CheckoutPaymentData, Transport } from "@shared/components/checkout/checkoutPayment.vue";
import { SharedPaymentsService } from "../core/services/sharedPaymentsService";
import CheckoutSummary from "@shared/components/checkout/checkoutSummary.vue";
import { BidirectionalIterator } from "@shared/core/bidirectionalIterator";
import CheckoutForm from "@shared/components/checkout/checkoutForm.vue";
import CheckoutTransport from "@shared/components/checkout/checkoutTransport.vue";
import { Component, Watch } from "@modules/vue-property-decorator";

enum CheckoutStep {
  Form,
  Summary,
  Transport,
  Payment,
  Ticket,
}

@Component({
  components: {
    CheckoutForm,
    CheckoutSummary,
    CheckoutTransport,
    CheckoutPayment,
  },
})
export default class CheckoutPage extends Vue {
  private readonly CheckoutStep = CheckoutStep;
  private stepper: BidirectionalIterator<CheckoutStep> = new BidirectionalIterator<CheckoutStep>([]);
  private data: CheckoutPaymentData | null = null;
  private cid: number | null = null;
  private selectedTransport: Transport|null = null;
  private discountInfo: {promoCode: string, discount: number, netDiscount: number, discountedTaxes: number} = {
    promoCode: "",
    discount: 0,
    netDiscount: 0,
    discountedTaxes: 0,
  };

  private get showDeliveryInfo() {
    return (
      !this.$store.state.loggedIn ||
      (this.data &&
        this.data.transports !== null &&
        this.sItems !== undefined &&
        this.sItems !== null &&
        this.sItems.length != 0)
    );
  }

  private get checkTransportRequired() {
    return this.sItems.some(
      (y) => y.units > 0 && y.product!.transportRequired === true
    );
  }
  private get sItems() {
    return this.$store.state.cart.sItems;
  }
  private get sales() {
    return this.$store.state.cart.sales;
  }

  @Watch('sItems')
  @Watch('sales')
  private onCartChanged() {
    if (!this.sItems?.length && !this.sales?.length) {
      this.$router.push('/');
    }
  }

  private onPromoApplied(payload: typeof this.discountInfo) {
    this.discountInfo = payload;
  }

  private onStepBack() {
    if (this.stepper.back().done) {
      this.$router.back();
    }
  }

  private onStepNext() {
    if (this.stepper.next().done) {
      this.$router.push('/');
    }
  }

  async mounted() {
    if (LocalStorage.get("checkoutParams")) {
      this.initStepper(new Set([CheckoutStep.Payment, CheckoutStep.Ticket]));
      return;
    }
    let payload: any = {};

    let sid = LocalStorage.getWithTimeout("cc");
    if (sid !== undefined && sid !== null) {
      this.cid = JSON.parse(sid).id;
    }

    if (
      this.$store.state.cart === null ||
      this.$store.state.cart === undefined
    ) {
      return this.$router.push("/");
    }

    if (this.$store.state.cart.count === 0) {
      return this.$router.push("/");
    }

    let i = 0;
    for (const saleItem of this.sItems) {
      saleItem.saleId = --i; // saleId tmp to identify
    }

    payload = {
      customerId: this.cid,
      saleItems: this.sItems,
      saleIds: this.sales.map((y) => y.saleIds).flat(),
    };

    let url =
      this.cid == null
        ? `${Settings.HostName}/api/shop/checkout-info`
        : `${Settings.HostName}/api/shop/checkout-info-by-customer`;

    let res = await new CrudService().httpPost(url, payload);
    if (!res) {
      console.error("Crash getting checkout info " + this.cid);
      return;
    }

    if (res.ok === false) {
      console.error(res.errorMsg);
      return;
    }

    const data = res.data as CheckoutPaymentData;
    if (!data) {
      console.error(JSON.stringify(res));
      return;
    }

    this.data = data;

    this.initStepper();

    const sale = this.sales[0];
    data.allowVouchers = { allowed: true, reason: null } ;
    const isSharedPayment = sale?.paymentType === BookingPaymentType.Shared;
    if (isSharedPayment) {
      const withRetention = sale.withRetention === true;      
      data.isSharedPayment = true;
      data.withRetention = withRetention;

      const svc = new SharedPaymentsService(this);
      const pm = data.paymentMethods;
      data.paymentMethods = svc.filterAllowedPaymentMethods(pm, withRetention);
      data.allowVouchers = svc.allowVouchers(pm, withRetention);
    }

    this.selectedTransport = this.data.transports?.at(0)!;
  }

  private initStepper(customSteps: Set<CheckoutStep>|null = null) {
    const steps: Set<CheckoutStep> = customSteps ?? new Set();
    if (!customSteps) {
      if (this.data && (this.showDeliveryInfo || this.checkTransportRequired)) {
        steps.add(CheckoutStep.Form);
      }
      steps.add(CheckoutStep.Summary);
      if (this.data && this.checkTransportRequired) {
        steps.add(CheckoutStep.Transport);
      }
      steps.add(CheckoutStep.Payment);
      steps.add(CheckoutStep.Ticket);
    }
    this.stepper = BidirectionalIterator.fromIterator(steps.values());
    this.stepper.next();
  }
}
